import { useEffect, useState } from 'react';

import { cn } from '@/lib/utils';
import { formatCountdown } from '@/utils/formatCountdown';
import { RiveDumpster } from '@/components/RiveDumpster';

interface SeasonCountdownCardProps {
  seasonNo: number;
  seasonEndTime: string; // ISO timestamp
  onBack?: () => void; // Go back to NFT card
  onJoinNow: () => void; // Navigate to /points
  onClose: () => void; // Close entire card stack
  className?: string;
}

export function SeasonCountdownCard({
  seasonEndTime,
  onBack,
  onJoinNow,
  onClose,
  className,
}: SeasonCountdownCardProps) {
  const [countdown, setCountdown] = useState('--');

  useEffect(() => {
    // Update countdown every second
    const updateCountdown = () => {
      const formatted = formatCountdown(seasonEndTime);
      setCountdown(formatted);
    };

    // Initial update
    updateCountdown();

    // Set up interval
    const interval = setInterval(updateCountdown, 1000);

    return () => clearInterval(interval);
  }, [seasonEndTime]);

  return (
    <div
      className={cn('relative overflow-hidden', className)}
      style={{
        width: 500,
        height: 620,
        background: '#1B1B1B',
        boxShadow: '5px 4px 50px rgba(0, 0, 0, 0.75)',
        border: '2px solid #50E4F9',
        borderRadius: 40,
      }}
    >
      {/* Background image */}
      <img
        src="/Plano Futurista em Ciano.png"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          zIndex: 0,
          borderRadius: 40,
        }}
      />

      {/* Back Button */}
      {onBack && (
        <button
          onClick={onBack}
          className="absolute top-4 left-4 z-20 p-2 rounded-full transition-all hover:scale-110 active:scale-95"
          style={{
            background: 'rgba(80, 228, 249, 0.2)',
            border: '2px solid #50E4F9',
            backdropFilter: 'blur(10px)',
          }}
          aria-label="Go back"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M19 12H5M5 12L12 19M5 12L12 5"
              stroke="#50E4F9"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      )}

      {/* Content - Using absolute positioning for precise control */}
      <div className="relative z-10" style={{ width: '100%', height: '100%' }}>
        {/* Header */}
        <h1
          className="text-center"
          style={{
            fontFamily: 'Hyperspace Race Capsule, sans-serif',
            fontSize: 30,
            fontWeight: 'bold',
            color: 'white',
            textTransform: 'uppercase',
            letterSpacing: '1.5px',
            lineHeight: '1.3',
            paddingTop: '20px',
            paddingLeft: '70px',
            paddingRight: '50px',
          }}
        >
          New Lucky Draw Season
        </h1>

        {/* Chest Animation - Absolutely positioned */}
        <div
          style={{
            position: 'absolute',
            top: '10px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 450,
            height: 450,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <RiveDumpster width={420} height={420} isOpen={true} />
        </div>

        {/* Countdown Label */}
        <div
          className="text-center"
          style={{
            position: 'absolute',
            top: '430px',
            left: 0,
            right: 0,
            fontFamily: 'Outfit, sans-serif',
            fontSize: 22,
            fontWeight: '600',
            color: '#50E4F9',
            letterSpacing: '1px',
          }}
        >
          Ending Countdown
        </div>

        {/* Countdown Timer */}
        <div
          className="text-center"
          style={{
            position: 'absolute',
            top: '460px',
            left: 0,
            right: 0,
            fontFamily: 'Outfit, sans-serif',
            fontSize: 28,
            fontWeight: '700',
            color: 'white',
            letterSpacing: '1px',
          }}
        >
          {countdown}
        </div>

        {/* Bottom border separator - Full width */}
        <div
          style={{
            position: 'absolute',
            bottom: 92,
            left: 0,
            right: 0,
            height: 2,
            background: '#50E4F9',
          }}
        />

        {/* Action Buttons */}
        <div
          className="flex items-center justify-center gap-3"
          style={{
            position: 'absolute',
            bottom: 20,
            left: 0,
            right: 0,
          }}
        >
          {/* Join Now Button */}
          <button
            onClick={onJoinNow}
            className="px-5 py-2.5 rounded-full transition-all hover:scale-105 active:scale-95"
            style={{
              background:
                'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
              fontFamily: 'Hyperspace Race Capsule, sans-serif',
              fontSize: 15,
              fontWeight: 'bold',
              color: '#1B1B1B',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              boxShadow: '0 4px 20px rgba(251, 139, 0, 0.4)',
              minWidth: 160,
            }}
          >
            Join Now
          </button>

          {/* Close Button */}
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-full transition-all hover:scale-105 active:scale-95"
            style={{
              background: 'transparent',
              border: '2px solid white',
              fontFamily: 'Hyperspace Race Capsule, sans-serif',
              fontSize: 15,
              fontWeight: 'bold',
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              minWidth: 120,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
